"use client";

import * as React from "react";
import { TriangleAlert as AlertTriangle, ArrowRight, CircleCheck as CheckCircle2, Clock, Droplets, Flame, Gauge, PhoneCall, ShieldCheck, ThermometerSnowflake, Volume2, Wind } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const problemsList = [{
  id: "no-hot-water",
  title: "No Hot Water at All",
  severity: "High Priority",
  severityVariant: "destructive",
  iconName: "flame",
  summary: "Complete loss of heating capability across all household taps.",
  commonCauses: "Tripped circuit breaker, failed heating element, or extinguished pilot light.",
  dangerNote: "May indicate electrical shorting or a gas supply valve fault."
}, {
  id: "tank-leaks",
  title: "Water Leaking From Tank Base",
  severity: "Emergency",
  severityVariant: "destructive",
  iconName: "droplets",
  summary: "Standing water or active pooling around the tank perimeter.",
  commonCauses: "Internal tank corrosion, loose drain valve, or failed pressure relief valve.",
  dangerNote: "Presents immediate flood risk. Requires immediate water supply shutoff."
}, {
  id: "temp-inconsistency",
  title: "Inconsistent Temperature",
  severity: "Moderate",
  severityVariant: "secondary",
  iconName: "thermometer",
  summary: "Water turns lukewarm quickly or fluctuates unpredictably between hot and cold.",
  commonCauses: "Broken dip tube, sediment accumulation, or failing upper thermostat.",
  dangerNote: "Excessive scale buildup lowers efficiency and accelerates tank wear."
}, {
  id: "popping-noises",
  title: "Popping or Rumbling Noises",
  severity: "Moderate",
  severityVariant: "secondary",
  iconName: "volume",
  summary: "Loud boiling, knocking, or crackling sounds during heating cycles.",
  commonCauses: "Heavy mineral deposits trapping boiling steam pockets at tank base.",
  dangerNote: "Unchecked sediment leads to overheating and premature tank rupture."
}, {
  id: "low-pressure",
  title: "Low Hot Water Pressure",
  severity: "Low to Medium",
  severityVariant: "outline",
  iconName: "gauge",
  summary: "Cold water flow is strong, but hot water trickles weakly from faucets.",
  commonCauses: "Mineral buildup in hot outlet lines or corroded shutoff valves.",
  dangerNote: "Restricts hot water delivery and strains the plumbing distribution system."
}, {
  id: "discolored-odor",
  title: "Rusty Water or Foul Odor",
  severity: "High Priority",
  severityVariant: "secondary",
  iconName: "wind",
  summary: "Reddish-brown tinted water or rotten egg sulfur smells coming from hot taps.",
  commonCauses: "Depleted sacrificial anode rod reacting with water bacteria, or tank rust.",
  dangerNote: "Rust in water usually signals advancing internal tank deterioration."
}];
const checklistItems = [{
  id: "step-1",
  title: "Locate Shutoff Valve",
  detail: "Turn the cold water inlet handle clockwise on top of the tank to stop incoming supply."
}, {
  id: "step-2",
  title: "Cut Power or Gas",
  detail: "Switch off the dedicated breaker or rotate the gas control knob to OFF."
}, {
  id: "step-3",
  title: "Call Emergency Dispatch",
  detail: "Contact our certified Jonesboro technicians for rapid same-day diagnosis and repair."
}];
export default function WaterProblems() {
  const prefersReducedMotion = useReducedMotion();
  const renderIcon = (name: string) => {
    switch (name) {
      case "flame":
        return <Flame className="size-6 text-destructive" />;
      case "droplets":
        return <Droplets className="size-6 text-primary" />;
      case "thermometer":
        return <ThermometerSnowflake className="size-6 text-accent-foreground" />;
      case "volume":
        return <Volume2 className="size-6 text-primary" />;
      case "gauge":
        return <Gauge className="size-6 text-accent-foreground" />;
      case "wind":
        return <Wind className="size-6 text-destructive" />;
      default:
        return <AlertTriangle className="size-6 text-primary" />;
    }
  };
  return <section id="waterheaterproblems" data-nav="light" className="bg-background text-foreground py-16 sm:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Block */}
        <div className="max-w-3xl mx-auto text-center mb-14">
          <Badge variant="outline" className="mb-3 px-3 py-1 font-semibold text-xs tracking-wide uppercase border-primary/20 text-primary">
            Troubleshooting Guide
          </Badge>
          <h2 className="font-default text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground">
            Common Water Heater Problems
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            From sudden ice-cold showers to pooling base leaks, water heater malfunctions disrupt your daily routine. Identify your system's symptoms below to determine whether you need a fast repair or a full replacement.
          </p>
        </div>

        {/* Emergency Alert Banner */}
        <div className="mb-12">
          <Alert variant="destructive" className="border-destructive/30 bg-destructive/5 text-foreground">
            <AlertTriangle className="text-destructive size-5" />
            <AlertTitle className="font-semibold text-base text-foreground">
              Smell Gas or Notice Heavy Water Flooding?
            </AlertTitle>
            <AlertDescription className="text-muted-foreground mt-1 text-sm sm:text-base">
              If your water heater is actively leaking gallons of water or you detect a strong natural gas odor, immediately shut off the main supply valve, vacate the area, and call our 24/7 Jonesboro emergency dispatch line.
            </AlertDescription>
          </Alert>
        </div>

        {/* Problems Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {problemsList.map((item, i) => <motion.div key={item.id} data-index={i} initial={prefersReducedMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 20
        }} whileInView={prefersReducedMotion ? {
          opacity: 1
        } : {
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: i * 0.08
        }} className="flex">
              <Card className="flex flex-col justify-between w-full border border-border shadow-sm hover:shadow-md transition-shadow">
                <CardHeader>
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="p-2.5 rounded-lg bg-muted flex items-center justify-center">
                      {renderIcon(item.iconName)}
                    </div>
                    <Badge variant={item.severityVariant === "destructive" ? "destructive" : item.severityVariant === "secondary" ? "secondary" : "outline"}>
                      {item.severity}
                    </Badge>
                  </div>
                  <CardTitle className="font-default text-xl font-bold text-foreground">
                    {item.title}
                  </CardTitle>
                  <CardDescription className="text-muted-foreground text-sm leading-relaxed mt-1">
                    {item.summary}
                  </CardDescription>
                </CardHeader>

                <CardContent className="space-y-4 text-sm">
                  <div className="p-3 rounded-md bg-secondary/50 border border-border/50">
                    <span className="font-semibold text-secondary-foreground block mb-1">
                      Primary Cause:
                    </span>
                    <p className="text-muted-foreground">{item.commonCauses}</p>
                  </div>

                  <div className="text-xs text-muted-foreground flex items-start gap-2">
                    <ShieldCheck className="size-4 shrink-0 text-primary mt-0.5" />
                    <span>{item.dangerNote}</span>
                  </div>
                </CardContent>

                <CardFooter className="pt-2">
                  <Button asChild variant="outline" className="w-full justify-between">
                    <Link to="/contact/">
                      <span>Request Diagnosis</span>
                      <ArrowRight className="size-4" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>)}
        </div>

        {/* Action Panel & Steps */}
        <div className="mt-14 rounded-2xl bg-card border border-border p-6 sm:p-10 shadow-sm">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7 space-y-4">
              <div className="flex items-center gap-2 text-primary font-semibold text-sm">
                <Clock className="size-4" />
                <span>Immediate Safety Checklist</span>
              </div>
              <h3 className="font-default text-2xl sm:text-3xl font-bold text-card-foreground">
                What to Do Before the Plumber Arrives
              </h3>
              <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                Taking proactive safety measures minimizes property damage and prevents electrical or gas hazards while our team travels to your home in Jonesboro.
              </p>

              <div className="space-y-3 pt-2">
                {checklistItems.map((step, idx) => <div key={step.id} data-index={idx} className="flex items-start gap-3">
                    <CheckCircle2 className="size-5 text-primary shrink-0 mt-0.5" />
                    <div>
                      <strong className="text-sm font-semibold text-foreground block">
                        {step.title}
                      </strong>
                      <span className="text-xs sm:text-sm text-muted-foreground">
                        {step.detail}
                      </span>
                    </div>
                  </div>)}
              </div>
            </div>

            <div className="lg:col-span-5 bg-background p-6 rounded-xl border border-border flex flex-col justify-between gap-6">
              <div>
                <span className="text-xs font-semibold text-primary uppercase tracking-wider block mb-1">
                  24/7 Jonesboro Dispatch
                </span>
                <h4 className="text-xl font-bold text-foreground">
                  Need Fast Water Heater Repairs?
                </h4>
                <p className="text-sm text-muted-foreground mt-2">
                  Our licensed technicians arrive fully equipped to inspect, flush, repair, or replace any tank or tankless system.
                </p>
              </div>

              <div className="space-y-3">
                <Button asChild size="lg" className="w-full justify-center">
                  <a href="tel:4708479076" className="flex items-center gap-2">
                    <PhoneCall className="size-4" />
                    <span>Call Emergency Line</span>
                  </a>
                </Button>

                <Button asChild variant="outline" size="lg" className="w-full justify-center">
                  <Link to="/water-heater/">
                    <span>View Water Heater Services</span>
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}